import type { DateTime } from "luxon";
import { getOrParseDateTime } from "@cpt/shared";
import { OrderStatus, SalesMotion } from "client/api-v2";
import type { OrderSummaryDto } from "client/api-v2";
import { quoteExpiryWarningDays, subscriptionTermWarningDays } from "areas/orders/orderConstants";

// The API sends these members as LocalDate strings, and the axios interceptor
// turns them into DateTime instances at runtime; getOrParseDateTime takes both.
function calendarDaysFromToday(localDate: string, today: DateTime): number {
  return getOrParseDateTime(localDate).startOf("day").diff(today.startOf("day"), "days").days;
}

/**
 * True when a published quote's validity window closes within the warning
 * threshold. `validUntil` is inclusive - the customer has all of that day - so
 * the count reaches zero on the last acceptable day. A lapsed date keeps the
 * warning until the expiry timer moves the status. Only a published quote
 * warns: a draft has no validity date, and a closed order's date names a
 * window that no longer applies.
 * @param order
 * @param today
 */
export function isQuoteExpiringSoon(order: OrderSummaryDto, today: DateTime): boolean {
  return (
    order.status === OrderStatus.Quote &&
    order.validUntil !== null &&
    calendarDaysFromToday(order.validUntil, today) <= quoteExpiryWarningDays
  );
}

/**
 * True when an unaccepted renewal's term start is within the warning
 * threshold. On a renewal the new term starts when the customer's current
 * subscription term lapses, so a near term start means the customer is about
 * to lose cover while the order is still open. A new sale has no lapsing
 * subscription, so it never warns.
 * @param order
 * @param today
 */
export function isSubscriptionTermEndingSoon(order: OrderSummaryDto, today: DateTime): boolean {
  return (
    order.salesMotion === SalesMotion.Renewal &&
    (order.status === OrderStatus.New || order.status === OrderStatus.Quote) &&
    calendarDaysFromToday(order.termStartDate, today) <= subscriptionTermWarningDays
  );
}
